import { useContext } from 'react';
import { makeStyles } from '@mui/styles';
import { translate } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import { alpha, darken } from '@classify/react-core-components/styles';
import { BackLink } from '@openclassrooms/ui-web/ui';
import { ROUTES } from '@openclassrooms/routing';
import HeroAction from './HeroAction';
import HeroAside from './HeroAside';
import CourseContext from '../../contexts/courseContext';
import CourseMetadata from '../CourseMetadata';

const useStyles = makeStyles(
  ({ spacing, breakpoints }) => ({
    wrapper: {
      padding: spacing(3, 3, 5, 3),
      [breakpoints.up('sm')]: {
        padding: spacing(3, 4, 7, 4),
      },
      [breakpoints.up('md')]: {
        padding: spacing(4, 7, 11, 7),
      },
    },
    innerWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column-reverse',
      marginTop: spacing(5),
      [breakpoints.up('md')]: {
        marginTop: 0,
        flexDirection: 'row',
      },
    },
    category: {
      textTransform: 'uppercase',
    },
    content: {
      flex: 1,
      marginTop: spacing(4),
      '& > * + *': {
        marginTop: spacing(2),
      },
      [breakpoints.up('md')]: {
        marginRight: spacing(3),
        '& > * + *': {
          marginTop: spacing(2),
        },
      },
      [breakpoints.up('lg')]: {
        marginRight: spacing(4),
      },
    },
    aside: {
      flex: 1,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      [breakpoints.up('md')]: {
        justifyContent: 'flex-end',
      },
    },
    actionsWrapper: {
      marginTop: spacing(4),
    },
  }),
  { classNamePrefix: 'Hero' }
);

function Hero() {
  const classes = useStyles();
  const { course } = useContext(CourseContext);

  const {
    title,
    categoryColor,
    category,
    userCourseCompletionRate: progression,
    shortDescription,
  } = course;

  const hasCourseStarted = progression && progression > 0;

  const backgroundColor = alpha(categoryColor, 0.08);
  const accessibleTitleColor = darken(categoryColor, 0.05);

  return (
    <div className={classes.wrapper} style={{ backgroundColor }} id="course-hero">
      <ContentWrapper>
        <BackLink href={ROUTES.COURSES.INDEX} component="a">
          {translate('course.home.intro.back.button')}
        </BackLink>

        <div className={classes.innerWrapper}>
          <div className={classes.content}>
            <div>
              <Typography
                variant="subheadingSmall"
                component="p"
                style={{ color: accessibleTitleColor, textTransform: 'uppercase' }}
              >
                {category}
              </Typography>
              <Typography variant="h2" component="h1">
                {title}
              </Typography>
            </div>

            {shortDescription && !hasCourseStarted && (
              <Typography variant="body">{shortDescription}</Typography>
            )}

            {!hasCourseStarted && <CourseMetadata />}

            <div className={classes.actionsWrapper}>
              <HeroAction />
            </div>
          </div>
          <div className={classes.aside}>
            <HeroAside />
          </div>
        </div>
      </ContentWrapper>
    </div>
  );
}

export default Hero;
